import { Notifier } from "@airbrake/browser";

import ahoy from "ahoy.js";

//import "@hotwired/turbo-rails";
import "@hotwired/turbo";

// Registers the <relative-time> custom element used for conversations' timestamps, which then
// keeps its own text live-updating (e.g. "vor 5 Minuten" -> "vor 6 Minuten") without any glue
// code here - it's a plain Web Component and re-initializes itself on every Turbo page swap.
import "@github/relative-time-element";

const env = ((window as any).env as string | undefined) || "production";

const airbrake = new Notifier({
  //host: "https://err.dsble.de",
  projectId: 19985,
  projectKey: "56b3c0cea61950684f91feb0460308d9",
  //projectKey: "edcdd8563076f741d56cfd05cc2daa44",
  environment: env,
  remoteConfig: env === "production",
  performanceStats: env === "production",
  queueStats: env === "production",
  queryStats: env === "production",
});
airbrake.addFilter((notice) => {
  if (notice.errors?.some((error) => error.message === "Transition was aborted because of invalid state")) {
    return null;
  }

  // Endlosrekursion in Google Translates eigenem Code (siehe translated_page.ts), nicht in unserem.
  const translated = /\btranslated-(ltr|rtl)\b/.test(document.documentElement.className);
  if (translated && notice.errors?.some((error) => error.type === "RangeError")) {
    return null;
  }

  return notice;
});
(window as any).airbrake = airbrake;

import { post } from "@rails/request.js";
(window as any).post = post;

import Alpine from "alpinejs";
(window as any).Alpine = Alpine;
import ui from "@alpinejs/ui";
Alpine.plugin(ui);
import focus from "@alpinejs/focus";
Alpine.plugin(focus);
import collapse from "@alpinejs/collapse";
Alpine.plugin(collapse);
import persist from "@alpinejs/persist";
Alpine.plugin(persist);
import blurhash from "../alpine-blurhash.ts";
Alpine.plugin(blurhash);
// Dynamic rather than a plain static import: a static import that fails to load (a network
// hiccup, a dev-server/HMR issue, anything) takes the *whole* module graph down with it per ES
// module semantics - including Alpine.start() below, even though message drafting has nothing to
// do with Alpine itself starting up at all. Awaiting it (rather than firing-and-forgetting) still
// keeps the ordering Alpine.data() needs - registered before Alpine.start()'s initial scan reaches
// any x-data="messageDraft(...)" element - but the .catch() means a failure here only breaks
// drafting/composing, logged as its own error, rather than silently taking every Alpine component
// on the page down with it.
await import("../message_draft.ts").catch((error) => {
  console.error("Failed to load message_draft.ts - composing/drafting messages won't work until reload", error);
  airbrake.notify(error);
});
// Same reasoning as message_draft.ts immediately above - registers the new-post draft/publish
// Alpine components before Alpine.start()'s initial scan reaches app/views/posts/new.html.haml's
// x-data="postDraft(...)"/"publishPost()" elements, without a failure here taking anything else
// down with it.
await import("../post_draft.ts").catch((error) => {
  console.error("Failed to load post_draft.ts - the new-post form's draft/publish won't work until reload", error);
  airbrake.notify(error);
});
// Same reasoning as the two above - registers the x-data="backLink" component before
// Alpine.start()'s scan reaches the back links on posts/show and users/show. Faellt das hier aus,
// bleibt der Link trotzdem benutzbar: er traegt ein echtes href und fuehrt dann eben auf die
// Liste statt einen Schritt im Verlauf zurueck.
await import("../back_link.ts").catch((error) => {
  console.error("Failed to load back_link.ts - the back link falls back to its plain href", error);
  airbrake.notify(error);
});

Alpine.start();

ahoy.configure({
  visitsUrl: "/bhoy/visits",
  eventsUrl: "/bhoy/events",
});
ahoy.trackView();
ahoy.trackClicks("a, button, input[type=submit]");
ahoy.trackSubmits("form");
ahoy.trackChanges("input, textarea, select");

// import "@uscreentv/turbo-train";
// Same reasoning as message_draft.ts above: this only wires up the live Mercure/SSE connection
// (see turbo-train.ts's connectedCallback) - a real, independent feature that should be able to
// fail (a broken/unreachable Mercure hub, the CORS issue tracked separately) without taking
// anything else on the page down with it. Nothing after this depends on its timing, so this one's
// fire-and-forget rather than awaited.
import("../turbo-train.ts").catch((error) => {
  console.error("Failed to load turbo-train.ts - live updates won't work until reload", error);
  airbrake.notify(error);
});

function readyAndLoaded() {
  // ShareComponent renders both the fallback (5 plain links) and native (big "Teilen" button +
  // dropdown) variants server-side; which one is actually visible is picked by CSS alone, via
  // the .navigator-share/.navigator-noshare class the layout's inline <head> script sets on
  // <html> (synchronously, before first paint - same trick as .os-macos). Only the *behavior*
  // needs to be gated here, and only because calling navigator.share() when it doesn't exist
  // would throw.
  if (navigator.share) {
    Array.from(document.querySelectorAll<HTMLLinkElement>("a.navigatorshare")).forEach((el) => {
      const shareData = {
        title: el.dataset.title,
        text: el.dataset.text,
        url: el.dataset.url,
      };
      // Read the shared item's identity off the button itself (set by ShareComponent) rather
      // than parsing it back out of the current URL - the old regex only ever matched a post's
      // numeric path (/123456) and silently produced no id for any other shared item.
      const resourceType = el.dataset.resourceType;
      const resourceId = el.dataset.resourceId;
      el.addEventListener("click", async (e) => {
        e.preventDefault();
        ahoy.track("share", {
          where: "navigator",
          clicked: true,
          resource_type: resourceType,
          resource_id: resourceId,
        });
        try {
          const res = await navigator.share!(shareData);
          ahoy.track("shared", {
            where: "navigator",
            success: true,
            resource_type: resourceType,
            resource_id: resourceId,
            res,
          });
        } catch (err) {
          ahoy.track("shared", {
            where: "navigator",
            success: false,
            resource_type: resourceType,
            resource_id: resourceId,
            err,
          });
        }
      });
    });
  }
  Array.from(document.querySelectorAll<HTMLLinkElement>("a.sharing-button:not(.navigatorshare)")).forEach((el) => {
    el.addEventListener("click", async () => {
      ahoy.track("share", {
        where: el.dataset.where,
        clicked: true,
        resource_type: el.dataset.resourceType,
        resource_id: el.dataset.resourceId,
      });
    });
  });
}

import { ready } from "../utils.ts";
ready(readyAndLoaded);

// Wie turbo-train.ts: Faellt das Hover-Dreieck aus, gehen die Kopfleisten-Menues wie bisher.
import("../hover_triangle.ts").catch((error) => {
  console.error("Failed to load hover_triangle.ts - header menus work without the hover triangle", error);
  airbrake.notify(error);
});

// Wie oben: Ohne das Modul bleibt ein Panel nach dem Klick offen, solange die Maus darueber steht.
import("../header_menu_click.ts").catch((error) => {
  console.error("Failed to load header_menu_click.ts - header panels stay open under the mouse after a click", error);
  airbrake.notify(error);
});

// Wie oben: Ohne das Modul laeuft Tab wie bisher durch jedes Panel der Kopfleiste.
import("../header_menu_keys.ts").catch((error) => {
  console.error("Failed to load header_menu_keys.ts - Tab walks through every header panel", error);
  airbrake.notify(error);
});

// Wie oben: Ohne das Mitnehmen gehen Meldungen, die hinter einem offenen Menue warteten, beim
// Seitenwechsel verloren - sonst aendert sich nichts.
import("../flash_carry_over.ts").catch((error) => {
  console.error("Failed to load flash_carry_over.ts - unseen flash messages won't follow a page change", error);
  airbrake.notify(error);
});

// Same fire-and-forget reasoning as turbo-train.ts above: the unread favicon dot is a nice-to-have
// that shouldn't take anything else down if it fails to load.
import("../favicon_unread_badge.ts")
  .then(({ setupFaviconUnreadBadge }) => ready(setupFaviconUnreadBadge))
  .catch((error) => {
    console.error("Failed to load favicon_unread_badge.ts - the unread favicon dot won't show", error);
    airbrake.notify(error);
  });

// Wie oben: Ohne das Modul stolpert eine von Google uebersetzte Seite nach ein paar Seitenwechseln.
import("../translated_page.ts").catch((error) => {
  console.error("Failed to load translated_page.ts - Turbo stays on for translated pages", error);
  airbrake.notify(error);
});

document.documentElement.addEventListener("turbo:fetch-request-error", (event) => {
  console.error("TURBO fetch-request-error", event);
});

if ("serviceWorker" in navigator) {
  // Register the service worker
  navigator.serviceWorker
    .register("/service-worker.js")
    .then(function (registration) {
      console.log("Service Worker registered with scope:", registration.scope);
    })
    .catch(function (error) {
      console.log("Service Worker registration failed:", error);
    });
}

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  (window as any).deferredPrompt = event;
  ahoy.track("beforeinstallprompt", { deferredPrompt: true });
});

window.addEventListener("appinstalled", () => {
  (window as any).deferredPrompt = null;
  localStorage.setItem("appInstalled", "true");
  ahoy.track("appinstalled");
});
